import { useCallback, useRef, useState } from "react"
import { useShareScheduledWorkflow } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowListItem } from "resources/scheduled-workflows/types"
import { shareErrorMessage } from "./activate-error"
import type { WorkflowShareSource } from "./workflow-share-dialog"

export interface OpenWorkflowShare {
  scheduledWorkflowId: string
  workflowName: string
  resourceId: string
  source: WorkflowShareSource
}

export type ShareableWorkflow = Pick<ScheduledWorkflowListItem, "scheduledWorkflowId" | "name">

export function useWorkflowShareLauncher() {
  const { mutateAsync } = useShareScheduledWorkflow(shareErrorMessage)
  const [openShare, setOpenShare] = useState<OpenWorkflowShare | null>(null)
  const pendingIdsRef = useRef(new Set<string>())
  const latestRequestRef = useRef(0)

  const startShare = useCallback(({ scheduledWorkflowId, name }: ShareableWorkflow, source: WorkflowShareSource) => {
    if (pendingIdsRef.current.has(scheduledWorkflowId)) return
    pendingIdsRef.current.add(scheduledWorkflowId)
    latestRequestRef.current += 1
    const request = latestRequestRef.current
    mutateAsync(scheduledWorkflowId)
      .then(
        ({ resourceId }) => {
          if (request === latestRequestRef.current) setOpenShare({ scheduledWorkflowId, workflowName: name, resourceId, source })
        },
        () => undefined,
      )
      .finally(() => { pendingIdsRef.current.delete(scheduledWorkflowId) })
  }, [mutateAsync])

  const closeShare = useCallback(() => setOpenShare(null), [])

  return { openShare, startShare, closeShare }
}
